// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// kinds.ts — the five creatures and their laws, kept in their own module so `modes.ts` (which
// composes armies out of them) and `rules.ts` (which enforces them) can both depend on it without
// depending on each other.
//
// A kind id is part of the wire protocol: it rides inside a placement ply. Never renumber these.

export const MATRIARCH = 0;
export const STRIDER = 1;
export const VAULTER = 2;
export const CLIMBER = 3;
export const WEAVER = 4;
export const TENDER = 5;

export const KIND_COUNT = 6;

export interface KindInfo {
  readonly kind: number;
  readonly name: string;
  /** One line, as printed in the help sheet and on the tile's own tooltip. */
  readonly law: string;
  /**
   * The mark engraved on the tile. Identity is carried by this SHAPE, so a player who cannot
   * separate the two seat colours can still read the whole board; colour is the redundant cue.
   */
  readonly glyph: string;
}

export const KINDS: readonly KindInfo[] = [
  {
    kind: MATRIARCH,
    name: 'Matriarch',
    law: 'Steps to one touching space. Close every side of her and her side loses.',
    glyph: 'M',
  },
  {
    kind: STRIDER,
    name: 'Strider',
    law: 'Walks any distance around the outside of the cluster.',
    glyph: 'S',
  },
  {
    kind: VAULTER,
    name: 'Vaulter',
    law: 'Leaps in a straight line over one or more tiles into the first free space beyond.',
    glyph: 'V',
  },
  {
    kind: CLIMBER,
    name: 'Climber',
    law: 'Steps one space, and may step up on top of a tile — whatever is underneath is pinned.',
    glyph: 'C',
  },
  {
    kind: WEAVER,
    name: 'Weaver',
    law: 'Walks exactly three spaces around the outside, never doubling back.',
    glyph: 'W',
  },
  {
    kind: TENDER,
    name: 'Tender',
    law: 'Steps one space — or lifts a touching tile of either colour over herself into a free space beside her.',
    glyph: 'T',
  },
];

export const kindInfo = (k: number): KindInfo => KINDS[k] ?? KINDS[MATRIARCH];

/** A tile packs its kind and its owner into one small integer. */
export const tile = (kind: number, seat: number): number => kind * 2 + seat;
export const tileKind = (t: number): number => (t / 2) | 0;
export const tileSeat = (t: number): number => t & 1;
